import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Button from '@ui/Button';
import PrimaryButtonWithDropdown from '@ui/Button/legacy/PrimaryButtonWithDropdown';
import Typography from '@ui/Typography';
import usePhonetic from '@replacements/hooks/usePhonetic';

import RespellingToggle from '@replacements/components/ReplacementsLibrary/ToolbarDrawer/Contents/RespellingToggle';
import PhoneticInput from '@replacements/components/PhoneticLibrary/PhoneticInput';
import { isInputValidRespelling } from '@replacements/utils/respellingUtil';
import useDictionaryReplacement from '@replacements/hooks/replacementsDictionary/useDictionaryReplacement';
import ReplacementsDictionary from '@replacements/components/ReplacementsDictionary';
import { useReplacementsDictionaryContext } from '@replacements/context/ReplacementsDictionaryContext';
import { useReplacementsLibraryContext } from '@replacements/context/ReplacementsLibraryContext';
import useProjectReplacements from '@replacements/core/useProjectReplacements';
import useBulkUpdateReplacementsInScript from '@replacements/hooks/useBulkUpdateReplacementsInScript';

import { useCanCreateTeamReplacements } from 'abilities/replacements';
import { ReplacementsDictionaryLocation } from '../../../../services/EventTrackingService';
import CollapsibleRow from '../../../CollapsibleRow';
import Tooltip from '../../../Tooltip';
import { toast } from '../../../Toaster';
import { PhoneticLibraryType } from '../../../../../server/graphql/__generated.types';
import { setReplacementsState } from '../../../../store/ReplacementsSlice';
import { useAppDispatch } from '../../../../store/hooks';
import { useLayoutContext } from '../../../BaseLayout/context/LayoutContext';

enum PhoneticLibraryLabel {
  Personal = 'Save to Personal',
  Team = 'Save to Team',
}

const useDictionaryStyles = makeStyles()(theme => ({
  dictionary: {
    position: 'absolute',
    top: theme.spacing(30),
    right: theme.spacing(35),
    display: 'grid',
  },
  // TODO: inspired by the StudioUtilityBar buttons.
  // We should consider building a reusable UtilityButton component.
  utilityButton: {
    height: 'auto',
    '&:hover, &:focus': {
      backgroundColor: theme.palette.grey[800],
    },
  },
}));

const PhoneticLibraryInputSidebar = ({
  projectId,
  supportsOxfordSuggestions,
}: {
  projectId?: string;
  supportsOxfordSuggestions?: boolean;
}) => {
  const appDispatch = useAppDispatch();

  const { projectReplacements } = useProjectReplacements({ projectId });
  const { applyBulkReplacementUpdate } = useBulkUpdateReplacementsInScript();
  // Snapshot projectReplacements at submit time so onSuccess sees the pre-mutation list,
  // not the post-cache-write list that Apollo synchronously delivers before onCompleted fires.
  const projectReplacementsAtSubmitRef = useRef(projectReplacements);
  const canCreateTeamReplacements = useCanCreateTeamReplacements();

  const [saveReplacementValue, setSaveReplacementValue] = useState<string>(
    () => {
      return canCreateTeamReplacements
        ? PhoneticLibraryLabel.Team
        : PhoneticLibraryLabel.Personal;
    }
  );
  const replacementInputRef = useRef<HTMLInputElement | null>(null);
  const {
    shouldFocusReplacement,
    setShouldFocusReplacement,
    defaultReplacementOriginalWord,
  } = useReplacementsLibraryContext();

  useEffect(() => {
    if (shouldFocusReplacement && replacementInputRef.current) {
      replacementInputRef.current.focus();
      setShouldFocusReplacement(false);
    }
  }, [shouldFocusReplacement, setShouldFocusReplacement]);

  const {
    error,
    loading,
    onSubmit,
    preview,
    replacement,
    isRespelling,
    setIsRespelling,
  } = usePhonetic({
    onSuccess: newReplacement => {
      toast({
        message: `Phonetic replacement "${replacement.text}" added for "${preview.text}".`,
        type: 'success',
      });
      appDispatch(setReplacementsState({ replacementsEnabled: true }));
      if (projectId) {
        const prev = projectReplacementsAtSubmitRef.current;
        applyBulkReplacementUpdate(prev, [...prev, newReplacement]);
      }
    },
    defaultPreviewWord: defaultReplacementOriginalWord,
  });

  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showRespellingMessage, setShowRespellingMessage] = useState(false);
  const { handleDictionaryIconClick } = useDictionaryReplacement({
    replacement,
    setIsRespelling,
  });
  const { toggleRightRailScrollable } = useLayoutContext();

  const onDictionaryClick = useCallback(() => {
    handleDictionaryIconClick(
      defaultReplacementOriginalWord || preview.text,
      ReplacementsDictionaryLocation.studio
    );
    toggleRightRailScrollable();
  }, [
    defaultReplacementOriginalWord,
    handleDictionaryIconClick,
    preview.text,
    toggleRightRailScrollable,
  ]);

  useEffect(() => {
    setShowRespellingMessage(false);
  }, [replacement.text, preview.text]);

  useEffect(() => {
    if (defaultReplacementOriginalWord) {
      onDictionaryClick();
    }
  }, [defaultReplacementOriginalWord, onDictionaryClick]);

  useEffect(() => {
    if (
      audioPlaying &&
      !isRespelling &&
      isInputValidRespelling(replacement.text)
    ) {
      setShowRespellingMessage(true);
    }
  }, [audioPlaying, isRespelling, replacement.text]);

  const handleRespellingToggle = (event: any) => {
    setIsRespelling(event.target.checked);
    // clears both cached previews (not just replacement's) since isRespelling
    // affects how the replacement is synthesized relative to the original word
    preview.onTextChange(preview.text);
    replacement.onTextChange(replacement.text);
    setShowRespellingMessage(false);
  };

  /**
   * @todo we should refactor the split dropdown to select by
   * value vs children
   */
  const handleButtonWithDropdownClick = useCallback(
    ({ value: dropdownLabel }: { value: string }) => {
      let updatedPhoneticLibraryType = PhoneticLibraryType.Personal;
      if (dropdownLabel === PhoneticLibraryLabel.Personal) {
        updatedPhoneticLibraryType = PhoneticLibraryType.Personal;
      }
      if (dropdownLabel === PhoneticLibraryLabel.Team) {
        updatedPhoneticLibraryType = PhoneticLibraryType.Team;
      }
      if (!preview.text || !replacement.text) return;
      projectReplacementsAtSubmitRef.current = projectReplacements;
      onSubmit(
        { preventDefault: () => {} } as React.FormEvent<HTMLFormElement>, // mock form event
        canCreateTeamReplacements ? updatedPhoneticLibraryType : undefined,
        projectId
      );
    },
    [
      onSubmit,
      preview.text,
      projectId,
      projectReplacements,
      replacement.text,
      canCreateTeamReplacements,
    ]
  );

  const handleIndividualUserSubmit = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    projectReplacementsAtSubmitRef.current = projectReplacements;
    onSubmit(e, PhoneticLibraryType.Personal, projectId);
  };

  const isDisabled = !replacement.text || !preview.text;

  const {
    suggestions,
    showDictionary,
    reset,
    loading: loadingSuggestions,
  } = useReplacementsDictionaryContext();

  const { classes } = useDictionaryStyles();

  return (
    <>
      <Box display={'flex'} alignItems={'center'} pb={1}>
        <Typography variant="pSm" bold>
          Original text
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" paddingBottom={3}>
        <PhoneticInput
          id="phonetic-input"
          name="phonetic-input"
          placeholder="Type original text"
          loading={loading}
          preview={preview}
          playbackProps={{
            label: 'preview input pronunciation',
            preview,
            location: 'Saved Word - Rail',
          }}
          onChange={reset}
        />
      </Box>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        mb={1}
      >
        <Typography variant="pSm" bold>
          Replacement
        </Typography>
        {supportsOxfordSuggestions && (
          <Tooltip text={'Find suggested respellings'}>
            <Button
              variant="icon_text"
              size="small"
              color="secondary"
              className={classes.utilityButton}
              iconType={'search'}
              style={{ whiteSpace: 'nowrap', textTransform: 'none' }}
              onClick={onDictionaryClick}
            >
              See suggestions
            </Button>
          </Tooltip>
        )}
      </Box>
      <ReplacementsDictionary
        style={{ display: showDictionary ? 'grid' : 'none' }}
        className={classes.dictionary}
        wordInput={preview.text}
        replacementInput={replacement.text}
        loading={loadingSuggestions}
        suggestions={suggestions}
        onClose={reset}
        location={'Replacement Dictionary - Studio Rail'}
      />
      <Box display="flex" alignItems="center">
        <PhoneticInput
          ref={replacementInputRef}
          id="phonetic-replacement"
          name="phonetic-replacement"
          placeholder="Type replacement text"
          loading={loading}
          preview={replacement}
          playbackProps={{
            label: 'preview replacement pronunciation',
            location: 'Saved Replacement - Rail',
            isRespelling,
            preview: replacement,
            original: preview,
            setAudioPlaying,
          }}
        />
      </Box>
      <RespellingToggle
        checked={isRespelling}
        onChange={handleRespellingToggle}
        errorMessage={
          error?.message ?? replacement.errorMsg ?? preview.errorMsg
        }
        location="editor"
        showRespellingMessage={showRespellingMessage}
      />
      {!canCreateTeamReplacements ? (
        <Box marginTop={2}>
          <Button
            variant="secondary"
            type="button"
            loading={loading}
            onClick={handleIndividualUserSubmit}
            size="small"
            fullWidth
            disabled={isDisabled}
          >
            {'Save To Library'}
          </Button>
        </Box>
      ) : (
        <Box marginTop={1.5}>
          <PrimaryButtonWithDropdown
            id="save-new-replacement-options"
            primaryButtonDisabled={isDisabled}
            selectDropdownDisabled={isDisabled}
            type="button"
            loading={loading}
            color={'secondary'}
            loadingText="Cancel"
            componentSize="small"
            buttonWidth={230}
            clickButtonOnSelect
            listItems={[
              {
                key: 'personal',
                value: PhoneticLibraryLabel.Personal,
                children: <div>{PhoneticLibraryLabel.Personal}</div>,
              },
              {
                key: 'team',
                value: PhoneticLibraryLabel.Team,
                children: <div>{PhoneticLibraryLabel.Team}</div>,
              },
            ]}
            selectedListItem={saveReplacementValue}
            onDropdownSelect={setSaveReplacementValue}
            onButtonClick={handleButtonWithDropdownClick}
            fullWidth
          />
        </Box>
      )}
    </>
  );
};

const PhoneticLibraryInputSidebarWrapper = ({
  projectId,
  disabled,
  supportsOxfordSuggestions = true,
}: {
  projectId?: string;
  disabled?: boolean;
  supportsOxfordSuggestions?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { setShowDictionary } = useReplacementsDictionaryContext();
  const {
    isNewReplacementSidebarAccordionOpen,
    setIsNewReplacementSidebarAccordionOpen,
  } = useReplacementsLibraryContext();

  const onOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    setIsNewReplacementSidebarAccordionOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setShowDictionary(false);
    }
  }, [open, setShowDictionary]);

  return (
    <CollapsibleRow
      id="wsl-new-replacements-section"
      title="New replacement"
      onOpen={onOpen}
      defaultOpen={isNewReplacementSidebarAccordionOpen}
      disabled={disabled}
    >
      <PhoneticLibraryInputSidebar
        projectId={projectId}
        supportsOxfordSuggestions={supportsOxfordSuggestions}
      />
    </CollapsibleRow>
  );
};

export default PhoneticLibraryInputSidebarWrapper;
